import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardBody,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";

import {
  useFetchCustomerAddressesQuery,
  useFetchCustomerOrdersQuery,
  useFetchCustomerQuery,
  useLazyFetchPossibleDuplicatesQuery,
  useUpdateCustomerMutation,
} from "../api/api";
import { getApiErrorStatus } from "../api/errorUtils";
import { useFetchCompanyUserDataQuery } from "../api/usersApi";
import { ORDER_COUNTRIES } from "../data/orderRegions";
import { useTranslation } from "../hooks/useTranslation";
import {
  avatarToneFor,
  buildCustomerProfilePatch,
  canEditCustomerProfile,
  buildEditDraftFromCustomer,
  customerInitials,
  describeCustomerSaveError,
  formatCustomerDate,
  formatCustomerTime,
  hasPhoneChanged,
  hasProfileErrors,
  phoneDigits,
  showingRange,
  validateCustomerProfile,
} from "./customerPresentation";
import { resolveOrderDisplayStatus, resolveOrderSourceLabel } from "./orderStatusPresentation";
import { resolveItemImage } from "./saleItemPresentation";
import { formatMoney, useDebounce } from "./salesUtils";

const ORDERS_PAGE_SIZE = 10;

const fill = (template, values) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, String(value)),
    String(template),
  );

const logApiError = (context, error) => {
  if (!error) return;
  // eslint-disable-next-line no-console
  console.warn(`[customers:${context}] API error`, error?.status, error?.data || error);
};

/**
 * Detajet e klientit: profile, real order statistics, where we delivered before,
 * and order history. Orders are opened in the real Orders page; this page never
 * duplicates the order drawer. Editing the profile never changes past orders.
 */
const CustomerDetail = () => {
  const t = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { customerId: customerIdParam } = useParams();
  const customerId = Number(customerIdParam);
  const isValidId = Number.isInteger(customerId) && customerId > 0;
  const listQuery = location.state?.listQuery || "";
  const backTo = `/admin/customers${listQuery ? `?${listQuery}` : ""}`;

  const [ordersPage, setOrdersPage] = useState(1);
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [touched, setTouched] = useState({});
  const [attempted, setAttempted] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const {
    data: detail,
    isFetching: isFetchingCustomer,
    error: customerError,
  } = useFetchCustomerQuery(customerId, { skip: !isValidId, refetchOnMountOrArgChange: true });
  const { data: addresses = [], error: addressesError } = useFetchCustomerAddressesQuery(customerId, {
    skip: !isValidId,
    refetchOnMountOrArgChange: true,
  });
  const {
    data: ordersData,
    isFetching: isFetchingOrders,
    error: ordersError,
  } = useFetchCustomerOrdersQuery(
    { customerId, page: ordersPage, pageSize: ORDERS_PAGE_SIZE },
    { skip: !isValidId, refetchOnMountOrArgChange: true },
  );
  // Same role source as the sidebar. The backend still enforces the permission.
  const { data: companyUser } = useFetchCompanyUserDataQuery();
  const canEdit = canEditCustomerProfile(companyUser?.user?.role);
  const [updateCustomer, { isLoading: isSaving }] = useUpdateCustomerMutation();
  const [checkDuplicates, { data: draftDuplicates = [], isFetching: isCheckingDuplicates }] =
    useLazyFetchPossibleDuplicatesQuery();

  const customer = detail?.customer || null;
  const possibleDuplicates = detail?.possibleDuplicates || [];
  const debouncedDraftPhone = useDebounce(draft?.phone || "", 400);
  const draftPhoneChanged =
    Boolean(customer && draft) &&
    hasPhoneChanged(customer, { phone: debouncedDraftPhone }) &&
    phoneDigits(debouncedDraftPhone).length >= 6;

  useEffect(() => {
    logApiError("detail", customerError);
  }, [customerError]);
  useEffect(() => {
    logApiError("orders", ordersError);
  }, [ordersError]);
  useEffect(() => {
    logApiError("addresses", addressesError);
  }, [addressesError]);

  // Warn while typing a new phone that another customer may already have it.
  useEffect(() => {
    if (editOpen && draftPhoneChanged) {
      checkDuplicates({ customerId, phone: debouncedDraftPhone });
    }
  }, [editOpen, draftPhoneChanged, customerId, debouncedDraftPhone, checkDuplicates]);

  const openEdit = () => {
    if (!canEdit) return;
    setDraft(buildEditDraftFromCustomer(customer));
    setTouched({});
    setAttempted(false);
    setSaveError(null);
    setEditOpen(true);
  };

  const closeEdit = () => {
    if (!isSaving) setEditOpen(false);
  };

  const errors = draft ? validateCustomerProfile(draft) : {};
  const fieldError = (field) => (attempted || touched[field] ? errors[field] : null);
  const updateDraft = (field, value) => setDraft((current) => ({ ...current, [field]: value }));
  const showDraftDuplicates = draftPhoneChanged && draftDuplicates.length > 0;

  const submitEdit = async () => {
    setAttempted(true);
    if (!draft || hasProfileErrors(validateCustomerProfile(draft))) {
      return;
    }

    const patch = buildCustomerProfilePatch(customer, draft);
    if (!Object.keys(patch).length) {
      setEditOpen(false);
      return;
    }

    setSaveError(null);
    try {
      await updateCustomer({ customerId, ...patch }).unwrap();
      setEditOpen(false);
      setFeedback(t("customers.saved"));
    } catch (error) {
      logApiError("update", error);
      setSaveError(t(describeCustomerSaveError(error)));
    }
  };

  const isNotFound = !isValidId || getApiErrorStatus(customerError) === 404;

  if (isNotFound || (customerError && !customer)) {
    return (
      <div className="content sales-premium customer-detail-page">
        <Link className="cd-back" to={backTo}>
          ← {t("customers.backToList")}
        </Link>
        <Card className="sales-page-card">
          <CardBody className="cu-empty">
            <div className="sp-empty-title">
              {isNotFound ? t("customers.notFound") : t("customers.detailLoadError")}
            </div>
            <p className="sp-empty-desc">
              {isNotFound ? t("customers.notFoundHint") : t("customers.loadErrorHint")}
            </p>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="content sales-premium customer-detail-page">
        <Link className="cd-back" to={backTo}>
          ← {t("customers.backToList")}
        </Link>
        <p className="sp-text-sm sp-text-muted">{isFetchingCustomer ? t("commerceCommon.loading") : ""}</p>
      </div>
    );
  }

  const { stats } = customer;
  const statItems = [
    { key: "total", value: stats.totalOrders, labelKey: "customers.statTotal", tone: "" },
    { key: "active", value: stats.activeOrders, labelKey: "customers.statActive", tone: "info" },
    { key: "delivered", value: stats.deliveredOrders, labelKey: "customers.statDelivered", tone: "ok" },
    { key: "cancelled", value: stats.cancelledOrders, labelKey: "customers.statCancelled", tone: "danger" },
    {
      key: "returned",
      value: stats.returnedToSenderOrders,
      labelKey: "customers.statReturned",
      tone: "warn",
    },
    {
      key: "last",
      value: formatCustomerDate(stats.lastOrderAt) || "—",
      labelKey: "customers.statLastOrder",
      tone: "date",
    },
  ];

  const orders = ordersData?.items || [];
  const ordersTotal = Number(ordersData?.total || 0);
  const ordersTotalPages = Math.max(1, Number(ordersData?.totalPages || 1));
  const ordersRange = showingRange({ page: ordersPage, pageSize: ORDERS_PAGE_SIZE, total: ordersTotal });

  const renderOrderRow = (order) => {
    const status = resolveOrderDisplayStatus(order);
    const [firstLine] = order.previewLines || [];
    const extraProducts = Math.max(0, Number(order.lineCount || 0) - 1);
    const orderLink = `/admin/orders?orderId=${order.id}&tab=all`;

    return (
      <div className="cd-order-row" key={order.id}>
        <Link className="cd-order-number" to={orderLink}>
          {order.orderNumber}
        </Link>
        <div className="cd-products">
          <span className="cd-thumbs">
            {(order.previewLines || []).map((line) => {
              const image = resolveItemImage(line);
              return (
                <span className="cd-thumb" key={line.id}>
                  {image ? <img src={image} alt="" loading="lazy" /> : null}
                </span>
              );
            })}
          </span>
          <span className="cd-product-text">
            <span className="cd-product-title">
              {firstLine ? `${firstLine.productTitle} × ${firstLine.quantity}` : "—"}
            </span>
            {extraProducts > 0 ? (
              <span className="cd-product-more">
                {extraProducts === 1
                  ? t("customers.moreProductsOne")
                  : fill(t("customers.moreProducts"), { count: extraProducts })}
              </span>
            ) : null}
          </span>
        </div>
        <span className="cd-source" data-label={t("customers.colSource")}>
          {resolveOrderSourceLabel(t, order)}
        </span>
        <span className="cd-date" data-label={t("customers.colDate")}>
          <span>{formatCustomerDate(order.createdAt) || "—"}</span>
          <span>{formatCustomerTime(order.createdAt)}</span>
        </span>
        <span className="cd-cod" data-label={t("customers.colCod")}>
          {formatMoney(order.codAmount, order.currencyCode)}
        </span>
        <span data-label={t("customers.colStatus")}>
          <span className={`cu-status cu-status--${status.tone}`}>{t(status.labelKey)}</span>
        </span>
        <Button
          size="sm"
          color="primary"
          outline
          className="cd-view-order"
          onClick={() => navigate(orderLink)}
        >
          <i className="tim-icons icon-zoom-split" aria-hidden="true" /> {t("customers.viewOrder")}
        </Button>
      </div>
    );
  };

  return (
    <div className="content sales-premium customer-detail-page">
      <Link className="cd-back" to={backTo}>
        ← {t("customers.backToList")}
      </Link>

      <div className="cd-page-head">
        <div>
          <h1 className="sp-title">{t("customers.detailTitle")}</h1>
          <p className="sp-subtitle">{t("customers.detailSubtitle")}</p>
        </div>
        {canEdit ? (
          <Button color="primary" outline onClick={openEdit}>
            <i className="tim-icons icon-pencil" aria-hidden="true" /> {t("customers.editCustomer")}
          </Button>
        ) : null}
      </div>

      {feedback ? (
        <Alert color="success" toggle={() => setFeedback(null)}>
          {feedback}
        </Alert>
      ) : null}

      <section className="cd-profile">
        <div className="cd-identity">
          <span className={`cu-avatar cu-avatar--${avatarToneFor(customer.name)}`} aria-hidden="true">
            {customerInitials(customer.name)}
          </span>
          <div className="cd-identity-text">
            <h2 className="cd-name">{customer.name}</h2>
            <div className="cd-fact">
              <i className="tim-icons icon-mobile" aria-hidden="true" />
              {customer.phone}
            </div>
            {customer.city ? (
              <div className="cd-fact">
                <i className="tim-icons icon-square-pin" aria-hidden="true" />
                {[customer.city, customer.country].filter(Boolean).join(", ")}
              </div>
            ) : null}
            {customer.email ? (
              <div className="cd-fact">
                <i className="tim-icons icon-email-85" aria-hidden="true" />
                {customer.email}
              </div>
            ) : null}
            {customer.createdAt ? (
              <div className="cd-fact">
                <i className="tim-icons icon-calendar-60" aria-hidden="true" />
                {fill(t("customers.customerSince"), { date: formatCustomerDate(customer.createdAt) })}
              </div>
            ) : null}
          </div>
        </div>

        <div className="cd-stats">
          {statItems.map((item) => (
            <div className="cd-stat" key={item.key}>
              <span className={`cd-stat-value${item.tone ? ` cd-stat-value--${item.tone}` : ""}`}>
                {item.value}
              </span>
              <span className="cd-stat-label">{t(item.labelKey)}</span>
            </div>
          ))}
        </div>
      </section>

      {possibleDuplicates.length ? (
        <section className="cd-duplicates" role="note">
          <strong>{t("customers.duplicateWarningTitle")}</strong>
          <p className="sp-text-sm mb-0">{t("customers.duplicateWarningHint")}</p>
          <ul>
            {possibleDuplicates.map((duplicate) => (
              <li key={duplicate.id}>
                <span className="sp-font-medium">{duplicate.name}</span>
                <span className="sp-text-sm">{duplicate.phone}</span>
                <span className="sp-text-sm sp-text-muted">
                  {fill(t("customers.duplicateOrders"), { count: duplicate.stats.totalOrders })}
                </span>
                <Link to={`/admin/customers/${duplicate.id}`} state={{ listQuery }}>
                  {t("customers.openCustomer")}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="cd-grid">
        <section className="cd-card">
          <h3 className="cd-card-title">
            <i className="tim-icons icon-square-pin" aria-hidden="true" /> {t("customers.addressesTitle")}
          </h3>
          <p className="cd-card-hint">{t("customers.addressesHint")}</p>
          {addressesError ? (
            <p className="sp-text-sm sp-text-muted">{t("customers.addressesLoadError")}</p>
          ) : addresses.length ? (
            <div className="cd-addresses">
              {addresses.map((entry) => (
                <div className="cd-address" key={`${entry.address}-${entry.city}-${entry.country}`}>
                  <i className="tim-icons icon-istanbul cd-address-icon" aria-hidden="true" />
                  <div>
                    <div className="cd-address-line">{entry.address}</div>
                    <div className="cd-address-meta">
                      {[entry.city, entry.country].filter(Boolean).join(", ")}
                    </div>
                    <div className="cd-address-meta">
                      {entry.uses === 1
                        ? t("customers.addressUsedOnce")
                        : fill(t("customers.addressUses"), { count: entry.uses })}
                      {entry.lastUsedAt
                        ? ` · ${fill(t("customers.addressLastUsed"), {
                            date: formatCustomerDate(entry.lastUsedAt),
                          })}`
                        : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="sp-text-sm sp-text-muted">{t("customers.noAddresses")}</p>
          )}
        </section>

        <section className="cd-card">
          <h3 className="cd-card-title">
            <i className="tim-icons icon-cart" aria-hidden="true" /> {t("customers.historyTitle")}
          </h3>

          {ordersError ? (
            <p className="sp-text-sm sp-text-muted">{t("customers.ordersLoadError")}</p>
          ) : isFetchingOrders && !ordersData ? (
            <p className="sp-text-sm sp-text-muted">{t("commerceCommon.loading")}</p>
          ) : orders.length ? (
            <>
              <div className="cd-orders">
                <div className="cd-order-row cd-order-row--header" aria-hidden="true">
                  <span>{t("customers.colOrder")}</span>
                  <span>{t("customers.colProducts")}</span>
                  <span>{t("customers.colSource")}</span>
                  <span>{t("customers.colDate")}</span>
                  <span>{t("customers.colCod")}</span>
                  <span>{t("customers.colStatus")}</span>
                  <span>{t("customers.colActions")}</span>
                </div>
                {orders.map(renderOrderRow)}
              </div>

              {ordersTotal > ORDERS_PAGE_SIZE ? (
                <div className="cu-pagination">
                  <span className="sp-text-sm sp-text-muted">
                    {fill(t("customers.ordersRange"), {
                      from: ordersRange.from,
                      to: ordersRange.to,
                      total: ordersTotal,
                    })}
                  </span>
                  <div className="cu-pagination-controls">
                    <Button
                      size="sm"
                      color="secondary"
                      outline
                      disabled={ordersPage <= 1 || isFetchingOrders}
                      onClick={() => setOrdersPage((current) => current - 1)}
                    >
                      ‹ {t("customers.previous")}
                    </Button>
                    <span className="cu-page-indicator">
                      {ordersPage} / {ordersTotalPages}
                    </span>
                    <Button
                      size="sm"
                      color="secondary"
                      outline
                      disabled={ordersPage >= ordersTotalPages || isFetchingOrders}
                      onClick={() => setOrdersPage((current) => current + 1)}
                    >
                      {t("customers.next")} ›
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <p className="sp-text-sm sp-text-muted">{t("customers.noOrders")}</p>
          )}
        </section>
      </div>

      <Modal isOpen={editOpen && canEdit} toggle={closeEdit} centered size="lg" className="ov-modal cu-modal">
        <ModalHeader toggle={closeEdit}>{t("customers.editTitle")}</ModalHeader>
        <ModalBody>
          {draft ? (
            <>
              <p className="sp-text-sm sp-text-muted">{t("customers.profileOnlyNote")}</p>
              <div className="ov-modal-grid">
                {[
                  ["name", "customers.fieldName", "text", true],
                  ["phone", "customers.fieldPhone", "tel", true],
                  ["email", "customers.fieldEmail", "email", false],
                  ["city", "customers.fieldCity", "text", true],
                ].map(([field, labelKey, type, required]) => (
                  <FormGroup key={field}>
                    <Label for={`customer-edit-${field}`}>
                      {t(labelKey)}
                      {required ? " *" : ""}
                    </Label>
                    <Input
                      id={`customer-edit-${field}`}
                      type={type}
                      autoComplete="off"
                      value={draft[field]}
                      invalid={Boolean(fieldError(field))}
                      onBlur={() => setTouched((current) => ({ ...current, [field]: true }))}
                      onChange={(event) => updateDraft(field, event.target.value)}
                    />
                    <FormFeedback>{fieldError(field) ? t(fieldError(field)) : null}</FormFeedback>
                  </FormGroup>
                ))}
                <FormGroup>
                  <Label for="customer-edit-country">{t("customers.fieldCountry")}</Label>
                  <Input
                    id="customer-edit-country"
                    type="select"
                    value={draft.country}
                    onChange={(event) => updateDraft("country", event.target.value)}
                  >
                    {!draft.country ? <option value="">—</option> : null}
                    {ORDER_COUNTRIES.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
                <FormGroup className="ov-modal-grid-wide">
                  <Label for="customer-edit-address">{t("customers.fieldAddress")} *</Label>
                  <Input
                    id="customer-edit-address"
                    type="textarea"
                    rows="2"
                    value={draft.address}
                    invalid={Boolean(fieldError("address"))}
                    onBlur={() => setTouched((current) => ({ ...current, address: true }))}
                    onChange={(event) => updateDraft("address", event.target.value)}
                  />
                  <FormFeedback>{fieldError("address") ? t(fieldError("address")) : null}</FormFeedback>
                </FormGroup>
              </div>

              {showDraftDuplicates ? (
                <div className="ov-modal-warning" role="alert">
                  <strong>{t("customers.duplicateWarningTitle")}</strong>
                  <p className="sp-text-sm mb-1">{t("customers.duplicateWarningHint")}</p>
                  <ul className="cu-duplicate-list">
                    {draftDuplicates.map((duplicate) => (
                      <li key={duplicate.id}>
                        {duplicate.name} · {duplicate.phone} ·{" "}
                        {fill(t("customers.duplicateOrders"), { count: duplicate.stats.totalOrders })}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {saveError ? <Alert color="danger" className="mt-3 mb-0">{saveError}</Alert> : null}
            </>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline disabled={isSaving} onClick={closeEdit}>
            {t("customers.cancel")}
          </Button>
          <Button color="primary" disabled={isSaving || isCheckingDuplicates} onClick={submitEdit}>
            {isSaving
              ? t("customers.saving")
              : showDraftDuplicates
                ? t("customers.saveAnyway")
                : t("customers.save")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default CustomerDetail;
